/**
 * "Kết thúc" step two — Task T2-E-web. Names the shift and the scope about to stop,
 * the same discipline `RevokeGrantPanel.tsx` (T2-D) states for its own: "are you
 * sure?" transfers no information; naming exactly what is about to change does.
 *
 * Lighter than `RevokeConfirm.tsx`'s own gate (no reason, no handle typed back) —
 * `DELETE /shift-assignments/{id}` never deletes the row (it sets `effective_to`/
 * `ended_by`/`ended_at`), and the attendance already computed against it stays
 * exactly as `endShiftAssignment`'s own docblock and `roster.end.body` state.
 *
 * `error` gets one special case: a `409 WORKFORCE_VERSION_CONFLICT` whose
 * `details.reason` is `'already_ended'` — a second `DELETE` on the same row, most
 * often from two tabs — renders the plain `roster.end.alreadyEnded` sentence instead
 * of the endpoint's generic "dữ liệu đã thay đổi ở nơi khác" copy, which talks about a
 * stale read that did not happen here (`api/roster.ts`'s own `isAlreadyEndedConflict`
 * docblock).
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { isAlreadyEndedConflict } from '../../api/roster';
import type { WorkforceApiError } from '../../api/errors';
import { DateField } from '../../components/DateField';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

export interface EndAssignmentPanelProps {
  readonly shiftLabel: string;
  readonly scopeLabel: string;
  readonly pending: boolean;
  readonly error: WorkforceApiError | null;
  readonly onConfirm: (effectiveTo: string) => void;
  readonly onCancel: () => void;
}

export function EndAssignmentPanel({
  shiftLabel,
  scopeLabel,
  pending,
  error,
  onConfirm,
  onCancel,
}: EndAssignmentPanelProps): ReactNode {
  const { t } = useI18n();
  const [effectiveTo, setEffectiveTo] = useState('');
  const alreadyEnded = error !== null && isAlreadyEndedConflict(error);

  return (
    <StatusNote
      severity="high"
      role="alert"
      title={t('roster.end.title', { shift: shiftLabel, subject: scopeLabel })}
      testId="t2ew-end"
      actions={
        <>
          <button
            type="button"
            className="wp-button wp-button--danger"
            data-testid="t2ew-end-confirm"
            disabled={pending}
            onClick={() => {
              onConfirm(effectiveTo);
            }}
          >
            {pending ? t('roster.end.confirming') : t('roster.end.confirm')}
          </button>
          <button
            type="button"
            className="wp-button wp-button--quiet"
            data-testid="t2ew-end-cancel"
            onClick={onCancel}
          >
            {t('roster.end.cancel')}
          </button>
        </>
      }
    >
      <p>{t('roster.end.body')}</p>

      <DateField
        id="t2ew-end-effective-to"
        testId="t2ew-end-effective-to"
        label={t('roster.end.effectiveTo')}
        value={effectiveTo}
        onChange={setEffectiveTo}
      />

      {error === null ? null : alreadyEnded ? (
        <StatusNote
          severity="info"
          title={t('roster.end.alreadyEnded')}
          testId="t2ew-end-already-ended"
        />
      ) : (
        <ApiErrorNote error={error} subject={t('roster.drawer.subject')} testId="t2ew-end-error" />
      )}
    </StatusNote>
  );
}
